import { ArrowForward, LockOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { authService } from "Apis";
import { useNotification } from "Hooks";
import { motion } from "motion/react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";

const fieldMotion = (delay: number) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay },
});

const fieldIconAdornment = (icon: ReactNode) => ({
  startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
});

interface IWooCommerceWelcomeFormProps {
  code: string;
}

function WooCommerceWelcomeForm({ code }: IWooCommerceWelcomeFormProps) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const history = useHistory();
  const { showErrorNotification } = useNotification();

  useEffect(() => {
    ValidatorForm.addValidationRule("isPasswordMatch", (value) => {
      return value === password;
    });
    return () => {
      ValidatorForm.removeValidationRule("isPasswordMatch");
    };
  }, [password]);

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await authService.authChangePasswordWithResetCode({
        body: {
          code,
          password,
          confirmation,
        },
      });
      history.push("/login");
    } catch {
      submittingRef.current = false;
      setSubmitting(false);
      showErrorNotification("Internal Server Error");
    }
  }, [code, password, confirmation, history, showErrorNotification]);

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
            <Chip
              label="for WooCommerce"
              size="small"
              sx={(theme) => ({
                fontWeight: 600,
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
                color: theme.palette.text.primary,
              })}
            />
            <Chip
              label="Almost there"
              size="small"
              sx={(theme) => ({
                fontWeight: 600,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              })}
            />
          </Stack>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
            Welcome to Incendium
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.75}>
            Your email is verified. Set a secure password to log in and connect
            your WooCommerce store.
          </Typography>
        </Box>

        <Stack spacing={3}>
          <motion.div {...fieldMotion(0.25)}>
            <TextValidator
              fullWidth
              type="password"
              name="password"
              label="Password"
              size="medium"
              value={password}
              variant="outlined"
              disabled={submitting}
              validators={["required"]}
              errorMessages={["Password is required"]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              InputProps={fieldIconAdornment(
                <LockOutlined fontSize="small" color="primary" />,
              )}
            />
          </motion.div>

          <motion.div {...fieldMotion(0.32)}>
            <TextValidator
              fullWidth
              type="password"
              name="confirmation"
              label="Confirm password"
              size="medium"
              value={confirmation}
              variant="outlined"
              disabled={submitting}
              validators={["required", "isPasswordMatch"]}
              errorMessages={["Password is required", "Passwords must match"]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfirmation(e.target.value)
              }
              InputProps={fieldIconAdornment(
                <LockOutlined fontSize="small" color="primary" />,
              )}
            />
          </motion.div>
        </Stack>

        <motion.div {...fieldMotion(0.39)}>
          <Button
            type="submit"
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            disabled={submitting}
            endIcon={<ArrowForward />}
            sx={{ py: 1.4, fontWeight: 700 }}
          >
            Set password & continue
          </Button>
        </motion.div>
      </Stack>
    </ValidatorForm>
  );
}

export default WooCommerceWelcomeForm;
